import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import * as I from 'immutable';
import React from 'react';

import {
  ApiFeature,
  CompositeAlertProperties,
  RangeCondition,
} from 'app/modules/Remote/Feature/types';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import colorUtils from 'app/utils/colorUtils';
import {DataLayerInfo} from 'app/utils/layers';
import * as layerUtils from 'app/utils/layerUtils';

import {RangeSlider} from '../../Sliders';
import {formatCompositeExpression, formatCondition} from '../alertPolicyUtils';
import {AlertPreviewChart} from '../AlertPreviewChart';
import {DatasetSelect} from '../DatasetSelect';
import cs from '../styles.module.scss';
import {
  AlertFormAction,
  AlertFormState,
  CompositeAlertFormState,
  MAX_CONDITIONS_PER_GROUP,
  MAX_CONDITION_GROUPS,
} from '../useAlertForm';

const conditionKey = (condition: RangeCondition) =>
  `${condition.layerId}:${condition.min}:${condition.max}`;

// The palette repeats via modulo, since a chart can have more bands than colors — up to 6 groups x
// 6 conditions can all land on the same layer — so every band still gets a color.
const CONDITION_BAND_COLORS = [
  colorUtils.darkRed,
  colorUtils.purple,
  colorUtils.brightBlue,
  colorUtils.brown,
  colorUtils.darkOrange,
  colorUtils.green,
];

// A group-wrapping paren is one formatCompositeExpression opens at the start of the string or
// right after "OR" while no other paren is already open; every other paren belongs to a layer name
// like "Vegetation (S2)" (whether or not any group wrap is present in the expression at all), and
// is tracked via a stack so its matching close gets the same treatment.
const splitExpressionParts = (text: string): {part: string; accented: boolean}[] => {
  const groupParenStack: boolean[] = [];
  let lastToken: string | undefined;
  return text.split(/(\bAND\b|\bOR\b|[()])/).map((part) => {
    let accented = false;
    if (part === 'AND' || part === 'OR') {
      accented = true;
    } else if (part === '(') {
      const isGroupParen =
        groupParenStack.length === 0 && (lastToken === undefined || lastToken === 'OR');
      groupParenStack.push(isGroupParen);
      accented = isGroupParen;
    } else if (part === ')') {
      // Guards malformed input (an unmatched close) rather than crash.
      accented = groupParenStack.pop() ?? false;
    }
    if (part.trim() !== '') lastToken = part;
    return {part, accented};
  });
};

// Accents the expression's AND/OR keywords and its group-wrapping parens with the same weight and
// color, so the logic reads out of the sentence rather than needing the monospace-query treatment
// the layout replaced.
const ExpressionText: React.FC<{text: string}> = ({text}) => {
  const parts = React.useMemo(() => splitExpressionParts(text), [text]);
  return (
    <>
      {parts.map(({part, accented}, index) =>
        accented ? (
          <span key={index} className={cs.expressionOperator}>
            {part}
          </span>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        )
      )}
    </>
  );
};

// A pristine row is the normal starting state, not a mistake, so it gets the muted hint rather
// than a red error — but it does get one from the start. Blank bounds block Next, and saying
// nothing left the user with a greyed-out button and nothing on screen to explain it.
const getRowValidation = (
  condition: RangeCondition,
  touched: boolean
): {intent?: B.Intent; helperText?: string} => {
  if (condition.min !== null && condition.max !== null && condition.min > condition.max) {
    return {
      intent: B.Intent.DANGER,
      helperText: "Lower bound can't be above the upper bound.",
    };
  }
  if (condition.min === null && condition.max === null) {
    return {
      intent: touched ? B.Intent.DANGER : undefined,
      helperText: 'Set at least one bound to continue.',
    };
  }
  return {};
};

export const CompositeAlertStep: React.FC<{
  disableInputs: boolean;
  formState: AlertFormState;
  dispatch: React.Dispatch<AlertFormAction>;
  feature: ApiFeature | undefined;
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  availableLayerKeys: string[];
}> = React.memo(
  ({formState, dispatch, disableInputs, feature, featureCollection, availableLayerKeys}) => {
    const properties = formState.properties as unknown as CompositeAlertProperties;
    const {conditionGroupIds} = formState as CompositeAlertFormState;
    const {sourceId} = layerUtils.parseLayerKey(formState.layer.key);

    const [touchedConditionIds, setTouchedConditionIds] = React.useState<ReadonlySet<string>>(
      new Set()
    );
    // Open on arrival, the way the threshold step shows its chart without asking. A chart nobody
    // expands explains nothing, and the modal prefetches these for composite policies too.
    const [previewsOpen, setPreviewsOpen] = React.useState(true);

    // Conditions are checked on the same pixel of the same scene, so every layer has to come off the
    // dataset the first condition picked. A range needs a numeric layer, which rules out the
    // category layers a threshold lookout can't use either.
    const sameSourceLayerKeys = React.useMemo(
      () =>
        availableLayerKeys.filter(
          (key) =>
            layerUtils.parseLayerKey(key).sourceId === sourceId &&
            layerUtils.getLayer(key).type === 'data'
        ),
      [availableLayerKeys, sourceId]
    );

    // Each chart computes its own raster time series, so conditions are grouped by layer across
    // every OR-group: a layer referenced by more than one condition, whether in one group or split
    // across two, computes and draws its series once. A layer's own bands still collapse on
    // (min, max), and a band that recurs in more than one group keeps every group index it came
    // from for the key below.
    const previewLayers = React.useMemo(() => {
      const bandsByLayer = new Map<
        string,
        Map<string, {condition: RangeCondition; groupIndexes: number[]}>
      >();
      properties.groups.forEach((group, groupIndex) => {
        group.forEach((condition) => {
          const bandsForLayer =
            bandsByLayer.get(condition.layerId) ??
            new Map<string, {condition: RangeCondition; groupIndexes: number[]}>();
          bandsByLayer.set(condition.layerId, bandsForLayer);
          const bandKey = conditionKey(condition);
          const existingBand = bandsForLayer.get(bandKey);
          if (existingBand) existingBand.groupIndexes.push(groupIndex);
          else bandsForLayer.set(bandKey, {condition, groupIndexes: [groupIndex]});
        });
      });

      return [...bandsByLayer.entries()].map(([layerId, bandsForLayer]) => ({
        layerId,
        bands: [...bandsForLayer.values()].map((band, index) => ({
          ...band,
          color: CONDITION_BAND_COLORS[index % CONDITION_BAND_COLORS.length],
        })),
      }));
    }, [properties.groups]);

    const layerForCondition = (layerId: string) =>
      layerUtils.getLayer(
        layerUtils.getLayerKeyFromSourceAndLayer(sourceId, layerId)
      ) as DataLayerInfo;

    // Duplicating the group's first layer produces "Vegetation AND Vegetation", and the two
    // identical rows then collapse into one preview chart, so the chart count disagrees with the
    // row count. Falls back to the first layer once every layer in the source is used.
    const nextUnusedLayerKey = (group: RangeCondition[]) => {
      const used = new Set(group.map((condition) => condition.layerId));
      return (
        sameSourceLayerKeys.find((key) => !used.has(layerUtils.parseLayerKey(key).layerId)) ??
        sameSourceLayerKeys[0]
      );
    };

    return (
      <div className={classnames(cs.dialogPanel, cs.compositeDialogPanel)}>
        {properties.groups.map((group, groupIndex) => (
          <React.Fragment key={conditionGroupIds[groupIndex]}>
            {groupIndex > 0 && (
              <div className={cs.groupOrDivider}>
                <span>OR</span>
              </div>
            )}
            <div className={cs.conditionGroup}>
              <div className={cs.conditionGroupHeader}>
                <span>
                  Group {groupIndex + 1}
                  {group.length > 1 && (
                    <span className={cs.conditionGroupSubtitle}>
                      {group.length} conditions, all must be true
                    </span>
                  )}
                </span>
                {properties.groups.length > 1 && (
                  <B.Button
                    minimal
                    small
                    text="Remove"
                    disabled={disableInputs}
                    onClick={() => dispatch({type: 'REMOVE_CONDITION_GROUP', groupIndex})}
                  />
                )}
              </div>

              <div className={cs.conditionRows}>
                {group.length > 1 && (
                  <div
                    className={cs.conditionBracket}
                    style={{gridRow: `1 / span ${group.length}`}}
                  >
                    <span
                      className={classnames(cs.conditionBracketTick, cs.conditionBracketTickTop)}
                    />
                    <span
                      className={classnames(cs.conditionBracketTick, cs.conditionBracketTickBottom)}
                    />
                    <span className={cs.conditionBracketOperator}>AND</span>
                  </div>
                )}
                {group.map((condition, conditionIndex) => {
                  const {intent, helperText} = getRowValidation(
                    condition,
                    touchedConditionIds.has(condition.id)
                  );
                  return (
                    <div className={cs.conditionRow} key={condition.id}>
                      <div className={cs.conditionLayerRow}>
                        <DatasetSelect
                          activeLayerInfo={layerForCondition(condition.layerId)}
                          availableLayerKeys={sameSourceLayerKeys}
                          disabled={disableInputs}
                          onItemSelect={(layerKey) =>
                            dispatch({
                              type: 'SET_CONDITION_LAYER',
                              groupIndex,
                              conditionIndex,
                              layerKey,
                            })
                          }
                        />
                        {/* Always rendered, so adding or removing a condition doesn't shift the
                          row's right edge — and the slider's width — underneath the user. */}
                        <B.Button
                          minimal
                          icon="cross"
                          aria-label="Remove condition"
                          title={
                            group.length > 1
                              ? 'Remove condition'
                              : 'A group needs at least one condition'
                          }
                          disabled={disableInputs || group.length === 1}
                          onClick={() =>
                            dispatch({type: 'REMOVE_CONDITION', groupIndex, conditionIndex})
                          }
                        />
                      </div>
                      <B.FormGroup
                        className={cs.conditionRangeGroup}
                        intent={intent}
                        helperText={helperText}
                      >
                        <RangeSlider
                          layer={layerForCondition(condition.layerId)}
                          min={condition.min}
                          max={condition.max}
                          disabled={disableInputs}
                          intent={intent}
                          boundLabelContext={`Group ${groupIndex + 1} condition ${conditionIndex + 1} ${
                            layerForCondition(condition.layerId).shortName
                          }`}
                          onChangeRange={({min, max}) => {
                            setTouchedConditionIds((prev) => new Set(prev).add(condition.id));
                            dispatch({
                              type: 'SET_CONDITION_RANGE',
                              groupIndex,
                              conditionIndex,
                              min,
                              max,
                            });
                          }}
                        />
                      </B.FormGroup>
                    </div>
                  );
                })}
              </div>

              <B.Tooltip
                content={`A group can hold up to ${MAX_CONDITIONS_PER_GROUP} conditions.`}
                disabled={group.length < MAX_CONDITIONS_PER_GROUP}
              >
                <B.Button
                  minimal
                  icon="plus"
                  text="Add condition"
                  disabled={disableInputs || group.length >= MAX_CONDITIONS_PER_GROUP}
                  onClick={() =>
                    dispatch({
                      type: 'ADD_CONDITION',
                      groupIndex,
                      layerKey: nextUnusedLayerKey(group),
                    })
                  }
                />
              </B.Tooltip>
            </div>
          </React.Fragment>
        ))}

        <B.Tooltip
          content={`A Lookout can hold up to ${MAX_CONDITION_GROUPS} alternative groups.`}
          disabled={properties.groups.length < MAX_CONDITION_GROUPS}
        >
          <B.Button
            minimal
            icon="plus"
            text="Add another group"
            disabled={disableInputs || properties.groups.length >= MAX_CONDITION_GROUPS}
            onClick={() => dispatch({type: 'ADD_CONDITION_GROUP'})}
          />
        </B.Tooltip>

        {feature && previewLayers.length > 0 && (
          <div className={cs.previewSection}>
            <B.Button
              minimal
              icon={previewsOpen ? 'chevron-up' : 'chevron-down'}
              text={
                previewsOpen
                  ? 'Hide condition history'
                  : `Show condition history (${previewLayers.length})`
              }
              onClick={() => setPreviewsOpen((open) => !open)}
            />
            <B.Collapse isOpen={previewsOpen}>
              <p className={cs.previewCaption}>
                One chart per layer, with every condition on that layer drawn as its own band. The
                Lookout fires only where every condition in a group holds on the same pixel, so
                these counts run higher than the policy's. These charts show data from the past
                calendar year (Jan 1 – Dec 31) for this property, or the last 3 data points if no
                data is available for that period.
              </p>
              {previewLayers.map(({layerId, bands}) => {
                const layer = layerForCondition(layerId);
                return (
                  <div className={cs.previewLayerChart} key={layerId}>
                    <AlertPreviewChart
                      feature={feature}
                      layer={layer}
                      featureCollection={featureCollection}
                      numConsecutiveScenes={properties.numConsecutiveScenes ?? 1}
                      title={layer.display}
                      showAlertCount={false}
                      bands={bands.map(({condition, color}) => ({
                        min: condition.min,
                        max: condition.max,
                        color,
                        label: formatCondition(sourceId, condition),
                      }))}
                    />
                    <ul className={cs.previewBandKey}>
                      {bands.map(({condition, groupIndexes, color}) => (
                        <li className={cs.previewBandKeyItem} key={conditionKey(condition)}>
                          <span
                            className={cs.previewBandSwatch}
                            style={{backgroundColor: color}}
                            aria-hidden
                          />
                          {groupIndexes.length > 1 ? 'Groups' : 'Group'}{' '}
                          {groupIndexes.map((groupIndex) => groupIndex + 1).join(', ')}:{' '}
                          {formatCondition(sourceId, condition)}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </B.Collapse>
          </div>
        )}

        {/* Not a field — nothing here is editable. It's the readout that verifies everything
            above it, so it's pinned to the panel's bottom edge rather than scrolling away. */}
        <div className={cs.firesWhenReadout}>
          <span className={cs.firesWhenLabel}>Fires when: </span>
          <div className={cs.firesWhenText}>
            <ExpressionText text={formatCompositeExpression(sourceId, properties)} />
          </div>
        </div>
      </div>
    );
  }
);
